import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatPrice, img } from "@/lib/api";
import { FREE_SHIPPING_THRESHOLD, STANDARD_SHIPPING_COST } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Checkout() {
  const { cart, cartTotal, cartCount } = useStore();
  const { user } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState({ email: user?.email || "", name: user?.name || "", address: "", city: "", country: "", zip: "" });
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);

  const shipping = cartTotal >= FREE_SHIPPING_THRESHOLD ? 0 : STANDARD_SHIPPING_COST;
  const discount = applied
    ? (applied.discount_type === "percent" ? cartTotal * (applied.value / 100) : Math.min(applied.value, cartTotal))
    : 0;
  const total = Math.max(0, cartTotal - discount) + shipping;

  const applyCoupon = async () => {
    const { data } = await api.post("/coupons/validate", { code: coupon });
    if (data.valid) {
      setApplied(data);
      const label = data.discount_type === "percent" ? `${data.value}% off` : `$${data.value} off`;
      toast.success(`Coupon applied: ${label}`);
    } else toast.error("Invalid or expired coupon code");
  };

  const pay = async (e) => {
    e.preventDefault();
    if (!cart.length) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: cart.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        origin_url: window.location.origin, email: info.email, coupon: applied?.code,
        shipping: info,
      });
      window.location.href = data.url;
    } catch (err) { toast.error("Could not start checkout"); setLoading(false); }
  };

  if (!cartCount) return (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-display text-3xl">Your bag is empty</h1>
      <Link to="/shop"><Button className="mt-6 rounded-none bg-[#D4AF37] text-black">Continue Shopping</Button></Link>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
      <h1 className="font-display text-4xl mb-10">Secure Checkout</h1>
      <div className="grid lg:grid-cols-5 gap-12">
        <form onSubmit={pay} className="lg:col-span-3 space-y-6" data-testid="checkout-form">
          <div>
            <h2 className="font-display text-xl mb-4">Contact & Shipping</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input required type="email" placeholder="Email" value={info.email} onChange={(e) => setInfo({...info, email: e.target.value})} className="rounded-none sm:col-span-2" data-testid="checkout-email" />
              <Input required placeholder="Full name" value={info.name} onChange={(e) => setInfo({...info, name: e.target.value})} className="rounded-none sm:col-span-2" data-testid="checkout-name" />
              <Input required placeholder="Address" value={info.address} onChange={(e) => setInfo({...info, address: e.target.value})} className="rounded-none sm:col-span-2" data-testid="checkout-address" />
              <Input required placeholder="City" value={info.city} onChange={(e) => setInfo({...info, city: e.target.value})} className="rounded-none" data-testid="checkout-city" />
              <Input required placeholder="ZIP / Postal" value={info.zip} onChange={(e) => setInfo({...info, zip: e.target.value})} className="rounded-none" data-testid="checkout-zip" />
              <Input required placeholder="Country" value={info.country} onChange={(e) => setInfo({...info, country: e.target.value})} className="rounded-none sm:col-span-2" data-testid="checkout-country" />
            </div>
          </div>
          {!user && <p className="text-sm text-muted-foreground">Checking out as guest. <Link to="/login" className="text-[#D4AF37] underline">Sign in</Link> to track orders.</p>}
          <Button disabled={loading} data-testid="pay-btn" className="w-full rounded-none h-13 py-6 bg-[#D4AF37] text-black font-semibold hover:brightness-110 flex items-center gap-2">
            <Lock className="h-4 w-4" />{loading ? "Redirecting to Stripe..." : `Pay ${formatPrice(total)}`}
          </Button>
          <p className="text-xs text-center text-muted-foreground">Payments secured by Stripe · Apple Pay & Google Pay supported at checkout</p>
        </form>

        <div className="lg:col-span-2">
          <div className="bg-[#F5F1E8] dark:bg-[#141414] p-6 space-y-4">
            <h2 className="font-display text-xl">Order Summary</h2>
            {cart.map((i) => (
              <div key={i.id} className="flex gap-3 text-sm">
                <img src={img(i.image)} alt={i.name} className="h-16 w-12 object-cover bg-background" />
                <div className="flex-1"><p className="font-medium">{i.name}</p><p className="text-muted-foreground">Qty {i.quantity}</p></div>
                <span>{formatPrice(i.price * i.quantity)}</span>
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <Input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code" className="rounded-none bg-background h-10" data-testid="coupon-input" />
              <Button type="button" onClick={applyCoupon} variant="outline" className="rounded-none h-10 border-black dark:border-white" data-testid="apply-coupon">Apply</Button>
            </div>
            <div className="border-t border-border pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>{formatPrice(cartTotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-[#D4AF37]"><span>Discount</span><span>−{formatPrice(discount)}</span></div>}
              <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : formatPrice(shipping)}</span></div>
              <div className="flex justify-between font-display text-lg pt-2 border-t border-border"><span>Total</span><span data-testid="order-total">{formatPrice(total)}</span></div>
            </div>
            <p className="text-xs text-muted-foreground">Try code <strong>WELCOME10</strong> for 10% off.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
